import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { AGREEMENT_KINDS, AGREEMENT_STATUSES } from '@mkcore/engagements/schema';
import { Button, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { AgreementDialog, agreementsQuery, dateOrDash } from '@/features/engagements/agreements';

const search = z.object({ q: z.string().optional(), kind: z.enum(AGREEMENT_KINDS).optional(), endsBefore: z.string().optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/agreements/')({ validateSearch: search, component: Agreements });

/** The contract register (CO-0177): agreements per status, the first to end first, with filters on kind and end date. */
function Agreements() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const canCreate = useCan('engagements:create');
  const [creating, setCreating] = useState(false);
  const q = useQuery(agreementsQuery({ q: s.q || undefined, kind: s.kind ? [s.kind] : undefined, endsBefore: s.endsBefore || undefined }));

  return (
    <>
      <PageHeader
        eyebrow={t('nav.engagements')}
        title={t('agreements.title')}
        description={t('agreements.description')}
        actions={
          canCreate && (
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('agreements.new')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('agreements.fields.kind')} className="w-52">
          {(p) => (
            <NativeSelect {...p} value={s.kind ?? ''} onChange={(e) => set({ kind: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {AGREEMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`agreements.kind.${k}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('agreements.endsBefore')} className="w-44">
          {(p) => <Input {...p} type="date" value={s.endsBefore ?? ''} onChange={(e) => set({ endsBefore: e.target.value || undefined })} />}
        </Field>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-64" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('agreements.empty')} description={canCreate ? t('agreements.emptyHint') : undefined} />
      ) : (
        AGREEMENT_STATUSES.map((status) => {
          const rows = q.data.items.filter((a) => a.status === status);
          if (!rows.length) return null;
          return (
            <section key={status} aria-label={t(`agreements.status.${status}`)} className="mb-6">
              <h2 className="mb-2 flex items-center gap-2 text-lg font-bold">
                {t(`agreements.status.${status}`)} <span className="text-sm font-normal text-fg-3">{rows.length}</span>
              </h2>
              <Table>
                <THead>
                  <Tr>
                    <Th>{t('agreements.fields.title')}</Th>
                    <Th>{t('agreements.fields.organisation')}</Th>
                    <Th>{t('agreements.fields.kind')}</Th>
                    <Th className="hidden md:table-cell">{t('agreements.fields.endsOn')}</Th>
                    <Th className="hidden md:table-cell">{t('agreements.fields.noticeOn')}</Th>
                    <Th className="hidden lg:table-cell">{t('agreements.fields.autoRenew')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {rows.map((a) => (
                    <Tr key={a.id}>
                      <Td>
                        <Link to="/agreements/$id" params={{ id: a.id }} className="font-bold hover:underline">
                          {a.title}
                        </Link>
                        {a.parentTitle && <span className="block text-xs text-fg-3">{t('agreements.under', { title: a.parentTitle })}</span>}
                      </Td>
                      <Td>
                        <Link to="/org/$id" params={{ id: a.organisationId }} className="hover:underline">
                          {a.organisationName}
                        </Link>
                      </Td>
                      <Td>
                        {t(`agreements.kind.${a.kind}`)}
                        {a.partyRole === 'supplier' && <span className="text-fg-3"> · {t('agreements.party.supplier')}</span>}
                      </Td>
                      <Td className="hidden tabular md:table-cell">{dateOrDash(a.endsOn)}</Td>
                      <Td className="hidden tabular md:table-cell">{dateOrDash(a.noticeOn)}</Td>
                      <Td className="hidden lg:table-cell">{a.autoRenew ? t('agreements.renewsBy', { months: a.renewalMonths }) : '–'}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </section>
          );
        })
      )}
      {creating && <AgreementDialog onClose={() => setCreating(false)} />}
    </>
  );
}
